/**
 * Ito Research TypeScript observability.
 *
 * A zero-dependency structured logging + tracing client. Events are shipped to
 * Axiom when credentials are configured, and degrade gracefully to the console
 * when they are not — so nothing fails in local dev or before credentials exist.
 *
 * Usage:
 *   const telemetry = initTelemetry("web");
 *   telemetry.info("app started", { port: 3000 });
 *   await telemetry.withSpan("render", async () => { ... });
 */

import { createTelemetry, type Telemetry } from "./telemetry";
import type { ObservabilityConfig, ObservabilityEnv } from "./config";
import type { LogContext, LogLevel, ActiveSpan, TraceContext } from "./telemetry";

export { createTelemetry } from "./telemetry";
export type {
  Telemetry,
  LogContext,
  LogLevel,
  ActiveSpan,
  TraceContext,
} from "./telemetry";
export { resolveConfig } from "./config";
export type { ObservabilityConfig, ObservabilityEnv } from "./config";
export { newTraceId, newSpanId, newEventId } from "./ids";

/**
 * Create (or reuse) the default telemetry client for a service.
 * Credentials come from the environment; when absent the client is a console
 * no-op and never throws.
 *
 * The singleton key includes the environment, so different environments get
 * their own client. A repeat call with a different config is ignored (with a
 * warning) — the first client for a key wins.
 */
export function initTelemetry(
  service: string,
  version = "0.1.0",
  env: ObservabilityEnv & Partial<ObservabilityConfig> = {},
): Telemetry {
  const envName = env.environment ?? env.NODE_ENV ?? "development";
  const key = `${service}@${version}@${envName}`;
  const signature = JSON.stringify(env);
  const existing = singletons.get(key);
  if (existing) {
    if (existing.signature !== signature) {
      console.warn(
        `[observability] initTelemetry("${service}") called again with a different config; ignoring the new config.`,
      );
    }
    return existing.client;
  }
  const client = createTelemetry(service, version, env);
  singletons.set(key, { client, signature });
  return client;
}

const singletons = new Map<string, { client: Telemetry; signature: string }>();
